(() => {
  'use strict';

  if (window.MBAppleCompare) return;

  const MBAppleCompare = {
    storageKey: 'mbAppleCompareSelectedV1',
    libraryJsonSelector: '[data-mb-apple-compare-library]',
    productJsonSelector: '[data-mb-apple-compare-product-json]',
    checkboxSelector: '[data-mb-apple-compare-checkbox]',
    tooltipSelector: '[data-mb-apple-compare-tooltip]',
    modalBreakpoint: 768,
    desktopLimit: 3,
    mobileLimit: 2,
    tooltipTimer: null,
    resizeTimer: null,
    refreshTimer: null,
    productsById: new Map(),
    selectedOptionsByProductId: new Map(),
    selectedProductIds: [],
    activeCompareGroup: '',
    activeColumnIds: [],
    loadedCompareGroups: new Set(),
    loadingCompareGroups: new Map(),
    libraryProductsByGroup: new Map(),
    compareGroupCollections: {
      'steam-showers': 'steam-showers',
      'whirlpool-tubs': 'whirlpool-tubs',
      'cold-plunge': 'cold-plunges',
      saunas: 'saunas',
    },
  };

  window.MBAppleCompare = MBAppleCompare;

  const dom = {};

  function cacheDom() {
    dom.root = document.querySelector('[data-mb-apple-compare-root]');
    dom.bar = document.querySelector('[data-mb-apple-compare-bar]');
    dom.openButton = document.querySelector('[data-mb-apple-compare-open]');
    dom.clearButton = document.querySelector('[data-mb-apple-compare-clear]');
    dom.count = document.querySelector('[data-mb-apple-compare-count]');
    dom.limit = document.querySelector('[data-mb-apple-compare-limit]');
    dom.groupLabel = document.querySelector('[data-mb-apple-compare-group-label]');
    dom.selectedProducts = document.querySelector('[data-mb-apple-compare-selected-products]');

    dom.modal = document.querySelector('[data-mb-apple-compare-modal]');
    dom.scroll = document.querySelector('[data-mb-apple-compare-scroll]');
    dom.closeButtons = Array.from(document.querySelectorAll('[data-mb-apple-compare-close]'));
    dom.title = document.querySelector('[data-mb-apple-compare-title]');
    dom.subtitle = document.querySelector('[data-mb-apple-compare-subtitle]');
    dom.selectors = document.querySelector('[data-mb-apple-compare-selectors]');
    dom.products = document.querySelector('[data-mb-apple-compare-products]');
    dom.specs = document.querySelector('[data-mb-apple-compare-specs]');
  }

  function getPageStorageKey() {
    return `${MBAppleCompare.storageKey}:${window.location.pathname}${window.location.search || ''}`;
  }

  function getCurrentLimit() {
    return window.matchMedia(`(max-width: ${MBAppleCompare.modalBreakpoint - 1}px)`).matches
      ? MBAppleCompare.mobileLimit
      : MBAppleCompare.desktopLimit;
  }

  function safeJsonParse(value, fallback) {
    try {
      return JSON.parse(value);
    } catch (_) {
      return fallback;
    }
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeId(value) {
    return String(value || '');
  }

  function normalizeCompareOptionKey(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function normalizeCompareColorKey(value) {
    const key = normalizeCompareOptionKey(value);

    const colorAliases = {
      grey: 'gray',
      'light-grey': 'gray',
      'dark-grey': 'gray',
      'light-gray': 'gray',
      'dark-gray': 'gray',
      'matte-black': 'black',
      'black-matte': 'black',
      chrome: 'gray',
      silver: 'gray',
    };

    return colorAliases[key] || key;
  }

  function normalizeCompareOrientationKey(value) {
    const key = normalizeCompareOptionKey(value);

    if (key.includes('left') || key === 'l') return 'left';
    if (key.includes('right') || key === 'r') return 'right';

    return '';
  }

  function formatMoneyFromCents(cents) {
    const number = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
        maximumFractionDigits: number % 1 === 0 ? 0 : 2,
      }).format(number);
    } catch (_) {
      return `$${number.toFixed(number % 1 === 0 ? 0 : 2)}`;
    }
  }

  function closestFromEvent(event, selector) {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    return target.closest(selector);
  }

  function getOptionIndex(product, keywords) {
    const options = Array.isArray(product?.options) ? product.options : [];

    return options.findIndex((optionName) => {
      const cleanName = String(optionName || '').toLowerCase();
      return keywords.some((keyword) => cleanName.includes(keyword));
    });
  }

  function getColorOptionIndex(product) {
    return getOptionIndex(product, ['color', 'colour', 'finish']);
  }

  function getOrientationOptionIndex(product) {
    return getOptionIndex(product, ['orientation']);
  }

  function getConfigurationOptionIndex(product) {
    return getOptionIndex(product, ['configuration', 'jet']);
  }

  function getWarrantyOptionIndex(product) {
    return getOptionIndex(product, ['warranty']);
  }

  function getVariantOptionValue(variant, optionIndex) {
    if (!variant || optionIndex < 0 || !Array.isArray(variant.options)) return '';
    return String(variant.options[optionIndex] || '');
  }

  function getStoredSelectedOptions(product, activeVariant) {
    const productId = normalizeId(product?.id);
    const storedOptions = MBAppleCompare.selectedOptionsByProductId.get(productId);

    if (Array.isArray(storedOptions) && storedOptions.length > 0) {
      return [...storedOptions];
    }

    if (Array.isArray(product?.selectedOptions) && product.selectedOptions.length > 0) {
      return [...product.selectedOptions];
    }

    if (Array.isArray(activeVariant?.options) && activeVariant.options.length > 0) {
      return [...activeVariant.options];
    }

    return [];
  }

  function getCompareImage(product, variant) {
    const compareImages = product?.compareImages || product?.compare_images;

    if (!compareImages || typeof compareImages !== 'object') return '';

    const colorOptionIndex = getColorOptionIndex(product);
    const orientationOptionIndex = getOrientationOptionIndex(product);
    const selectedOptions = getStoredSelectedOptions(product, variant);

    const colorValue =
      colorOptionIndex > -1
        ? selectedOptions[colorOptionIndex] || getVariantOptionValue(variant, colorOptionIndex)
        : '';

    const orientationValue =
      orientationOptionIndex > -1
        ? selectedOptions[orientationOptionIndex] || getVariantOptionValue(variant, orientationOptionIndex)
        : '';

    const colorKey = normalizeCompareColorKey(colorValue);
    const orientationKey = normalizeCompareOrientationKey(orientationValue) || 'right';

    const getImageFromColor = (colorImages) => {
      if (!colorImages || typeof colorImages !== 'object') return '';

      return (
        colorImages[orientationKey] ||
        colorImages.right ||
        colorImages.default ||
        colorImages.left ||
        ''
      );
    };

    const requestedImage = getImageFromColor(compareImages[colorKey]);
    if (requestedImage) return requestedImage;

    // Single-finish products may not expose a Color/Finish option.
    // Prefer the first populated card-image metafield before featured-media fallbacks.
    for (const fallbackColor of ['white', 'black', 'gray', 'red']) {
      const fallbackImage = getImageFromColor(compareImages[fallbackColor]);
      if (fallbackImage) return fallbackImage;
    }

    return '';
  }

  function getProductImage(product, variant) {
    return (
      getCompareImage(product, variant) ||
      variant?.featuredImage ||
      variant?.featured_image ||
      product?.featuredImage ||
      product?.featured_image ||
      product?.images?.[0] ||
      ''
    );
  }

  function getVariantPrice(variant, product) {
    return (
      variant?.priceFormatted ||
      variant?.price_formatted ||
      product?.priceFormatted ||
      product?.priceMinFormatted ||
      product?.price_min_formatted ||
      ''
    );
  }

  function getVariantComparePrice(variant, product) {
    return (
      variant?.compareAtPriceFormatted ||
      variant?.compare_at_price_formatted ||
      product?.compareAtPriceFormatted ||
      product?.compare_at_price_formatted ||
      ''
    );
  }

  function getDefaultVariant(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return variants.find((variant) => variant.available) || variants[0] || null;
  }

  function getVariantById(product, variantId) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return variants.find((variant) => normalizeId(variant.id) === normalizeId(variantId)) || getDefaultVariant(product);
  }

  function normalizeCompareGroup(value) {
    const group = String(value || '').trim().toLowerCase();

    if (
      group === 'bathtubs' ||
      group === 'bath-tubs' ||
      group === 'bath-tub' ||
      group === 'whirlpool-tub'
    ) {
      return 'whirlpool-tubs';
    }

    return group;
  }

  function getProductGroup(product) {
    return normalizeCompareGroup(product?.compareGroup || product?.compare_group) || 'default';
  }

  function getProductGroupLabel(product) {
    return product?.compareGroupLabel || product?.compare_group_label || product?.compareGroup || product?.compare_group || 'Products';
  }

  function getProductSpecs(product) {
    if (!Array.isArray(product?.specifications)) return [];

    return product.specifications
      .filter((spec) => spec && spec.label && String(spec.value || '').trim() !== '')
      .map((spec) => {
        const parsedOrder = Number(spec.order);

        return {
          key: spec.key || String(spec.label).toLowerCase().trim(),
          label: spec.label,
          order: spec.order !== '' && spec.order != null && Number.isFinite(parsedOrder)
            ? parsedOrder
            : Number.POSITIVE_INFINITY,
          value: spec.value,
        };
      });
  }

  function getColorFromName(name) {
    const key = String(name || '').toLowerCase().replace(/\s+/g, '-');

    const colorMap = {
      black: '#111111',
      'matte-black': '#111111',
      white: '#f5f5f5',
      grey: '#8c8c8c',
      gray: '#8c8c8c',
      silver: '#c9c9c9',
      chrome: '#c9c9c9',
      sand: '#d8c7aa',
      beige: '#d8c7aa',
      cream: '#eee1c8',
      ivory: '#f2ead8',
      blue: '#2d5f9a',
      navy: '#18233f',
      green: '#3b6f4a',
      red: '#9f2d2d',
      brown: '#6b4a2f',
      gold: '#c8a24a',
      'brushed-gold': '#c8a24a',
    };

    return colorMap[key] || key || '#d2d2d7';
  }

  function getProductSwatches(product) {
    if (Array.isArray(product?.swatches) && product.swatches.length > 0) {
      return product.swatches;
    }

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const seen = new Set();
    const swatches = [];

    variants.forEach((variant) => {
      const label = variant.option1 || variant.title || 'Default';
      const key = String(label).toLowerCase();

      if (seen.has(key)) return;
      seen.add(key);

      swatches.push({
        label,
        color: getColorFromName(label),
        variantId: variant.id,
        image: getProductImage(product, variant),
        priceFormatted: getVariantPrice(variant, product),
        compareAtPriceFormatted: getVariantComparePrice(variant, product),
        available: variant.available,
      });
    });

    return swatches;
  }

  function getOptionValues(product, optionIndex) {
    if (optionIndex < 0) return [];

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const values = [];

    variants.forEach((variant) => {
      const value = getVariantOptionValue(variant, optionIndex).trim();
      if (value && !values.includes(value)) {
        values.push(value);
      }
    });

    return values;
  }

  function setSelectedVariant(product, variant) {
    if (!product || !variant) return;

    product.selectedVariantId = normalizeId(variant.id);

    if (Array.isArray(variant.options)) {
      product.selectedOptions = [...variant.options];
      MBAppleCompare.selectedOptionsByProductId.set(normalizeId(product.id), [...variant.options]);
    }
  }

  function findVariantForOptionChange(product, optionIndex, optionValue) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const activeVariant = getVariantById(product, product.selectedVariantId);
    const activeOptions = getStoredSelectedOptions(product, activeVariant);

    activeOptions[optionIndex] = optionValue;

    const getMatchScore = (variant) => {
      if (!variant || !Array.isArray(variant.options)) return -1;

      let score = 0;

      variant.options.forEach((value, index) => {
        if (index === optionIndex) return;

        if (String(value || '') === String(activeOptions[index] || '')) {
          score += 1;
        }
      });

      return score;
    };

    const exactAvailableMatch = variants.find((variant) => {
      if (variant.available === false || !Array.isArray(variant.options)) return false;

      return variant.options.every((value, index) => {
        return String(value || '') === String(activeOptions[index] || '');
      });
    });

    if (exactAvailableMatch) return exactAvailableMatch;

    const exactMatch = variants.find((variant) => {
      if (!Array.isArray(variant.options)) return false;

      return variant.options.every((value, index) => {
        return String(value || '') === String(activeOptions[index] || '');
      });
    });

    if (exactMatch) return exactMatch;

    const sameOptionAvailableMatches = variants
      .filter((variant) => {
        return variant.available !== false && getVariantOptionValue(variant, optionIndex) === optionValue;
      })
      .sort((a, b) => getMatchScore(b) - getMatchScore(a));

    if (sameOptionAvailableMatches[0]) return sameOptionAvailableMatches[0];

    const sameOptionMatches = variants
      .filter((variant) => getVariantOptionValue(variant, optionIndex) === optionValue)
      .sort((a, b) => getMatchScore(b) - getMatchScore(a));

    if (sameOptionMatches[0]) return sameOptionMatches[0];

    return activeVariant || getDefaultVariant(product);
  }

  function renderProductOptionSelect(product, activeVariant, columnIndex, optionIndex, label) {
    const values = getOptionValues(product, optionIndex);
    if (optionIndex < 0 || values.length === 0) return '';

    const selectedOptions = getStoredSelectedOptions(product, activeVariant);
    const selectedValue = selectedOptions[optionIndex] || getVariantOptionValue(activeVariant, optionIndex) || values[0];

    return `
      <select
        class="mb-apple-compare-product__variant"
        data-mb-apple-compare-option-select
        data-column-index="${columnIndex}"
        data-product-id="${escapeHtml(product.id)}"
        data-option-index="${optionIndex}"
        aria-label="${escapeHtml(label)} for ${escapeHtml(product.title || '')}"
      >
        ${values.map((value) => `
          <option value="${escapeHtml(value)}"${value === selectedValue ? ' selected' : ''}>
            ${escapeHtml(value)}
          </option>
        `).join('')}
      </select>
    `;
  }

  function normalizeProduct(product) {
    if (!product || !product.id) return null;

    const normalizedProduct = { ...product };
    const defaultVariant = getDefaultVariant(normalizedProduct);

    normalizedProduct.id = normalizeId(normalizedProduct.id);
    normalizedProduct.compareGroup = getProductGroup(normalizedProduct);
    normalizedProduct.compareGroupLabel = getProductGroupLabel(normalizedProduct);
    normalizedProduct.selectedVariantId = normalizeId(
      normalizedProduct.selectedVariantId ||
      normalizedProduct.selected_variant_id ||
      defaultVariant?.id ||
      ''
    );

    if (Array.isArray(normalizedProduct.variants)) {
      normalizedProduct.variants = normalizedProduct.variants.map((variant) => ({
        ...variant,
        id: normalizeId(variant.id),
        productId: normalizeId(variant.productId || variant.product_id || normalizedProduct.id),
      }));
    }

    const selectedVariant = getVariantById(normalizedProduct, normalizedProduct.selectedVariantId);

    if (selectedVariant && Array.isArray(selectedVariant.options)) {
      normalizedProduct.selectedOptions = [...selectedVariant.options];
    }

    return normalizedProduct;
  }

  function addProductToMap(product) {
    const normalizedProduct = normalizeProduct(product);
    if (!normalizedProduct) return;

    const existingProduct = MBAppleCompare.productsById.get(normalizedProduct.id);

    const mergedProduct = {
      ...(existingProduct || {}),
      ...normalizedProduct,
    };

    if (existingProduct?.selectedVariantId) {
      mergedProduct.selectedVariantId = existingProduct.selectedVariantId;
    }

    const storedOptions = MBAppleCompare.selectedOptionsByProductId.get(normalizedProduct.id);

    if (Array.isArray(storedOptions) && storedOptions.length > 0) {
      mergedProduct.selectedOptions = [...storedOptions];
    } else if (Array.isArray(existingProduct?.selectedOptions) && existingProduct.selectedOptions.length > 0) {
      mergedProduct.selectedOptions = [...existingProduct.selectedOptions];
      MBAppleCompare.selectedOptionsByProductId.set(normalizedProduct.id, [...existingProduct.selectedOptions]);
    } else if (Array.isArray(mergedProduct.selectedOptions) && mergedProduct.selectedOptions.length > 0) {
      MBAppleCompare.selectedOptionsByProductId.set(normalizedProduct.id, [...mergedProduct.selectedOptions]);
    }

    MBAppleCompare.productsById.set(normalizedProduct.id, mergedProduct);
  }

  function collectLibraryProducts() {
    document.querySelectorAll(MBAppleCompare.libraryJsonSelector).forEach((script) => {
      const products = safeJsonParse(script.textContent, []);

      if (!Array.isArray(products)) return;

      products.forEach((product) => {
        addProductToMap(product);
      });
    });
  }

  function collectCardProducts() {
    document.querySelectorAll(MBAppleCompare.productJsonSelector).forEach((script) => {
      const product = safeJsonParse(script.textContent, null);
      addProductToMap(product);
    });
  }

  function collectCachedLibraryProducts() {
    MBAppleCompare.libraryProductsByGroup.forEach((products) => {
      products.forEach((product) => addProductToMap(product));
    });
  }

  function collectAllProducts() {
    MBAppleCompare.productsById.clear();

    collectLibraryProducts();
    collectCachedLibraryProducts();
    collectCardProducts();
  }

  function refreshCardProducts() {
    collectCardProducts();
  }

  function getCompareLibraryUrl(compareGroup) {
    const collectionHandle = MBAppleCompare.compareGroupCollections[compareGroup] || compareGroup;
    if (!collectionHandle || collectionHandle === 'default') return '';

    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;

    return `${normalizedRoot}collections/${encodeURIComponent(collectionHandle)}?section_id=mb-apple-compare-library`;
  }

  async function loadCompareGroup(compareGroup) {
    if (!compareGroup || compareGroup === 'default') return;

    if (MBAppleCompare.loadedCompareGroups.has(compareGroup)) return;

    if (MBAppleCompare.loadingCompareGroups.has(compareGroup)) {
      return MBAppleCompare.loadingCompareGroups.get(compareGroup);
    }

    const libraryUrl = getCompareLibraryUrl(compareGroup);
    if (!libraryUrl) return;

    const loadingPromise = fetch(libraryUrl, {
      headers: {
        Accept: 'text/html',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Compare library could not be loaded');
        }

        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const script = doc.querySelector(MBAppleCompare.libraryJsonSelector);
        const products = safeJsonParse(script?.textContent || '[]', []);

        if (Array.isArray(products)) {
          MBAppleCompare.libraryProductsByGroup.set(compareGroup, products);
          products.forEach((product) => {
            addProductToMap(product);
          });
        }

        MBAppleCompare.loadedCompareGroups.add(compareGroup);
      })
      .catch(() => {
        MBAppleCompare.loadedCompareGroups.delete(compareGroup);
        MBAppleCompare.libraryProductsByGroup.delete(compareGroup);
      })
      .finally(() => {
        MBAppleCompare.loadingCompareGroups.delete(compareGroup);
      });

    MBAppleCompare.loadingCompareGroups.set(compareGroup, loadingPromise);

    return loadingPromise;
  }

  function getProductsInGroup(compareGroup) {
    return Array.from(MBAppleCompare.productsById.values())
      .filter((product) => getProductGroup(product) === compareGroup)
      .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
  }

  function loadSelection() {
    const saved = safeJsonParse(localStorage.getItem(getPageStorageKey()), null);

    if (!saved || !Array.isArray(saved.productIds)) {
      MBAppleCompare.selectedProductIds = [];
      MBAppleCompare.activeCompareGroup = '';
      return;
    }

    MBAppleCompare.activeCompareGroup = normalizeCompareGroup(saved.compareGroup || '');
    MBAppleCompare.selectedProductIds = saved.productIds
      .map(normalizeId)
      .filter((id) => {
        const product = MBAppleCompare.productsById.get(id);
        if (!product) return false;
        return !MBAppleCompare.activeCompareGroup || getProductGroup(product) === MBAppleCompare.activeCompareGroup;
      });

    if (MBAppleCompare.selectedProductIds.length === 0) {
      MBAppleCompare.activeCompareGroup = '';
    }
  }

  function saveSelection() {
    if (MBAppleCompare.selectedProductIds.length === 0) {
      localStorage.removeItem(getPageStorageKey());
      return;
    }

    localStorage.setItem(
      getPageStorageKey(),
      JSON.stringify({
        compareGroup: MBAppleCompare.activeCompareGroup,
        productIds: MBAppleCompare.selectedProductIds,
      })
    );
  }

  function clearSelection() {
    MBAppleCompare.selectedProductIds = [];
    MBAppleCompare.activeCompareGroup = '';
    MBAppleCompare.activeColumnIds = [];

    saveSelection();
    syncCheckboxes();
    renderBar();
    closeModal();
  }

  function showTooltip(label, message) {
    if (!label) return;

    const tooltip = label.querySelector(MBAppleCompare.tooltipSelector);
    if (!tooltip) return;

    tooltip.textContent = message;
    tooltip.classList.add('is-visible');

    window.clearTimeout(MBAppleCompare.tooltipTimer);
    MBAppleCompare.tooltipTimer = window.setTimeout(() => {
      tooltip.classList.remove('is-visible');
    }, 2400);
  }

  function getCheckboxProduct(checkbox) {
    const productId = normalizeId(checkbox?.dataset?.productId);
    let product = MBAppleCompare.productsById.get(productId) || null;

    if (!product) {
      refreshCardProducts();
      product = MBAppleCompare.productsById.get(productId) || null;
    }

    if (product) return product;

    if (!checkbox || !productId) return null;

    const fallbackProduct = {
      id: productId,
      title: checkbox.dataset.productTitle || 'Selected product',
      url: checkbox.dataset.productUrl || '#',
      compareGroup: checkbox.dataset.compareGroup || 'default',
      compareGroupLabel: checkbox.dataset.compareGroupLabel || 'Products',
      variants: [],
      specifications: [],
      swatches: [],
    };

    addProductToMap(fallbackProduct);

    return fallbackProduct;
  }

  function syncCheckboxes() {
    refreshCardProducts();

    const limit = getCurrentLimit();
    const selectedSet = new Set(MBAppleCompare.selectedProductIds);

    document.querySelectorAll(MBAppleCompare.checkboxSelector).forEach((checkbox) => {
      const product = getCheckboxProduct(checkbox);
      const productId = normalizeId(checkbox.dataset.productId);
      const productGroup = product ? getProductGroup(product) : '';
      const isSelected = selectedSet.has(productId);
      const isWrongGroup = MBAppleCompare.activeCompareGroup && productGroup && productGroup !== MBAppleCompare.activeCompareGroup;
      const isFull = MBAppleCompare.selectedProductIds.length >= limit && !isSelected;

      checkbox.checked = isSelected;
      checkbox.disabled = Boolean(isWrongGroup || isFull);

      const label = checkbox.closest('label');
      if (label) {
        label.classList.toggle('is-disabled', checkbox.disabled && !isSelected);
      }
    });
  }

  function handleCheckboxChange(event) {
    refreshCardProducts();

    const checkbox = closestFromEvent(event, MBAppleCompare.checkboxSelector);
    if (!checkbox) return;

    const product = getCheckboxProduct(checkbox);

    if (!product) {
      checkbox.checked = false;
      return;
    }

    const productId = normalizeId(product.id);
    const productGroup = getProductGroup(product);
    const productGroupLabel = getProductGroupLabel(product);
    const limit = getCurrentLimit();
    const label = checkbox.closest('label');

    if (checkbox.checked) {
      if (MBAppleCompare.activeCompareGroup && MBAppleCompare.activeCompareGroup !== productGroup) {
        checkbox.checked = false;
        showTooltip(label, `You can only compare ${productGroupLabel} with other ${productGroupLabel}.`);
        syncCheckboxes();
        return;
      }

      if (MBAppleCompare.selectedProductIds.length >= limit && !MBAppleCompare.selectedProductIds.includes(productId)) {
        checkbox.checked = false;
        showTooltip(label, `You can compare up to ${limit} products at a time.`);
        syncCheckboxes();
        return;
      }

      MBAppleCompare.activeCompareGroup = productGroup;

      if (!MBAppleCompare.selectedProductIds.includes(productId)) {
        MBAppleCompare.selectedProductIds.push(productId);
      }

      saveSelection();
      syncCheckboxes();
      renderBar();

      loadCompareGroup(productGroup)
        .then(() => {
          syncCheckboxes();

          if (isModalOpen()) {
            renderModal();
          }
        })
        .catch(() => {
          syncCheckboxes();

          if (isModalOpen()) {
            renderModal();
          }
        });

      return;
    }

    MBAppleCompare.selectedProductIds = MBAppleCompare.selectedProductIds.filter((id) => id !== productId);

    if (MBAppleCompare.selectedProductIds.length === 0) {
      MBAppleCompare.activeCompareGroup = '';
    }

    saveSelection();
    syncCheckboxes();
    renderBar();

    if (isModalOpen()) {
      openModal();
    }
  }

  function handleCompareLabelClick(event) {
    const label = closestFromEvent(event, 'label');
    if (!label) return;

    const checkbox = label.querySelector(MBAppleCompare.checkboxSelector);
    if (!checkbox || !checkbox.disabled || checkbox.checked) return;

    const product = getCheckboxProduct(checkbox);
    if (!product) return;

    event.preventDefault();

    const productGroupLabel = getProductGroupLabel(product);
    const productGroup = getProductGroup(product);
    const limit = getCurrentLimit();

    if (MBAppleCompare.activeCompareGroup && MBAppleCompare.activeCompareGroup !== productGroup) {
      showTooltip(label, `You can only compare ${productGroupLabel} with other ${productGroupLabel}.`);
    } else {
      showTooltip(label, `You can compare up to ${limit} products at a time.`);
    }
  }

  function renderBar() {
    if (!dom.bar) return;

    const count = MBAppleCompare.selectedProductIds.length;
    const limit = getCurrentLimit();
    const selectedProducts = MBAppleCompare.selectedProductIds
      .map((id) => MBAppleCompare.productsById.get(id))
      .filter(Boolean);

    if (count === 0) {
      dom.bar.classList.remove('is-visible');
      dom.bar.setAttribute('aria-hidden', 'true');
      return;
    }

    const visibleSelectedProducts = selectedProducts.slice(0, limit);
    const firstProduct = visibleSelectedProducts[0] || selectedProducts[0];
    const groupLabel = firstProduct ? getProductGroupLabel(firstProduct) : 'Products';

    dom.bar.classList.add('is-visible');
    dom.bar.setAttribute('aria-hidden', 'false');

    if (dom.count) dom.count.textContent = String(Math.min(count, limit));
    if (dom.limit) dom.limit.textContent = String(limit);
    if (dom.groupLabel) dom.groupLabel.textContent = `Compare ${groupLabel}`;
    if (dom.selectedProducts) {
      dom.selectedProducts.textContent = visibleSelectedProducts.map((product) => product.title).join(' • ');
    }
  }

  function isModalOpen() {
    return dom.modal?.getAttribute('aria-hidden') === 'false';
  }

  async function openModal(event) {
    event?.preventDefault();

    refreshCardProducts();

    if (!dom.modal || MBAppleCompare.selectedProductIds.length === 0) return;

    const limit = getCurrentLimit();
    const selectedProducts = MBAppleCompare.selectedProductIds
      .map((id) => MBAppleCompare.productsById.get(id))
      .filter(Boolean);

    const firstProduct = selectedProducts[0];
    if (!firstProduct) return;

    MBAppleCompare.activeCompareGroup = getProductGroup(firstProduct);
    const requestedCompareGroup = MBAppleCompare.activeCompareGroup;

    MBAppleCompare.activeColumnIds = MBAppleCompare.selectedProductIds
      .slice(0, limit)
      .map(normalizeId);

    while (MBAppleCompare.activeColumnIds.length < limit) {
      MBAppleCompare.activeColumnIds.push('');
    }

    dom.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mb-apple-compare-is-open');

    renderModal();

    if (dom.scroll) dom.scroll.scrollTop = 0;

    loadCompareGroup(requestedCompareGroup)
      .then(() => {
        if (!isModalOpen() || MBAppleCompare.activeCompareGroup !== requestedCompareGroup) return;

        renderModal();
        syncCheckboxes();
        renderBar();
      })
      .catch(() => {
        if (!isModalOpen() || MBAppleCompare.activeCompareGroup !== requestedCompareGroup) return;

        renderModal();
      });
  }

  function closeModal() {
    if (!dom.modal) return;

    dom.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mb-apple-compare-is-open');
  }

  function renderModal() {
    const limit = getCurrentLimit();
    const groupProducts = getProductsInGroup(MBAppleCompare.activeCompareGroup);
    const groupLabel = groupProducts[0] ? getProductGroupLabel(groupProducts[0]) : 'Products';

    if (dom.title) dom.title.textContent = `Compare ${groupLabel}`;
    if (dom.subtitle) {
      dom.subtitle.textContent = `Choose up to ${limit} ${groupLabel.toLowerCase()} to compare images, colors, pricing, and technical specs.`;
    }

    renderProductSelectors(groupProducts, limit);
    renderProductSummaries(limit);
    renderSpecifications(limit);
  }

  function renderProductSelectors(groupProducts, limit) {
    if (!dom.selectors) return;

    dom.selectors.style.setProperty('--mb-apple-compare-columns', String(limit));

    let html = '';

    for (let index = 0; index < limit; index += 1) {
      const selectedId = MBAppleCompare.activeColumnIds[index] || '';
      const selectedElsewhere = MBAppleCompare.activeColumnIds
        .filter((id, idIndex) => idIndex !== index)
        .map(normalizeId);

      html += `
        <div class="mb-apple-compare-selector">
          <select class="mb-apple-compare-select" data-mb-apple-compare-column-select="${index}" aria-label="Select product ${index + 1}">
            <option value="">Choose product</option>
            ${groupProducts.map((product) => {
              const productId = normalizeId(product.id);
              const isSelected = productId === normalizeId(selectedId);
              const isUsedInOtherColumn = selectedElsewhere.includes(productId);

              return `
                <option value="${escapeHtml(productId)}"${isSelected ? ' selected' : ''}${isUsedInOtherColumn ? ' disabled' : ''}>
                  ${escapeHtml(product.title)}
                </option>
              `;
            }).join('')}
          </select>
        </div>
      `;
    }

    dom.selectors.innerHTML = html;
  }

  function renderProductSummaries(limit) {
    if (!dom.products) return;

    dom.products.style.setProperty('--mb-apple-compare-columns', String(limit));

    let html = '';

    for (let index = 0; index < limit; index += 1) {
      const productId = MBAppleCompare.activeColumnIds[index];
      const product = MBAppleCompare.productsById.get(normalizeId(productId));

      html += product ? renderProductColumn(product, index) : renderEmptyProductColumn();
    }

    dom.products.innerHTML = html;
  }

  function renderEmptyProductColumn() {
    return `
      <article class="mb-apple-compare-product is-empty">
        <div class="mb-apple-compare-product__image-wrap">
          <div class="mb-apple-compare-product__placeholder"></div>
        </div>
        <h3 class="mb-apple-compare-product__title">Choose a product</h3>
        <div class="mb-apple-compare-product__price">Select from the dropdown above.</div>
      </article>
    `;
  }

  function renderProductColumn(product, columnIndex) {
    const defaultVariant = getDefaultVariant(product);
    const activeVariant = getVariantById(product, product.selectedVariantId || defaultVariant?.id);
    const image = getProductImage(product, activeVariant);
    const price = getVariantPrice(activeVariant, product) || formatMoneyFromCents(product.priceMin || product.price_min || product.price || 0);
    const comparePrice = getVariantComparePrice(activeVariant, product);
    const swatches = getProductSwatches(product);
    const variants = Array.isArray(product.variants) ? product.variants : [];

    const colorOptionIndex = getColorOptionIndex(product);
    const orientationOptionIndex = getOrientationOptionIndex(product);
    const warrantyOptionIndex = getWarrantyOptionIndex(product);
    const configurationOptionIndex = getConfigurationOptionIndex(product);
    const selectedOptions = getStoredSelectedOptions(product, activeVariant);

    return `
      <article class="mb-apple-compare-product" data-mb-apple-compare-product-column="${columnIndex}" data-product-id="${escapeHtml(product.id)}">
        <div class="mb-apple-compare-product__image-wrap">
          ${image ? `
            <img class="mb-apple-compare-product__image" src="${escapeHtml(image)}" alt="${escapeHtml(product.title || '')}">
          ` : `
            <div class="mb-apple-compare-product__placeholder"></div>
          `}
        </div>

        <h3 class="mb-apple-compare-product__title">${escapeHtml(product.title || '')}</h3>

        <div class="mb-apple-compare-product__swatches" aria-label="Colors">
          ${swatches.map((swatch, swatchIndex) => {
            const swatchVariantId = normalizeId(swatch.variantId || swatch.variant_id);
            const swatchVariant = getVariantById(product, swatchVariantId);
            const activeColor = selectedOptions[colorOptionIndex] || getVariantOptionValue(activeVariant, colorOptionIndex);
            const swatchColor = getVariantOptionValue(swatchVariant, colorOptionIndex) || swatch.label || '';
            const isActive = colorOptionIndex > -1
              ? activeColor === swatchColor
              : swatchVariantId && normalizeId(activeVariant?.id) === swatchVariantId;

            return `
              <button
                class="mb-apple-compare-swatch${isActive || (!activeVariant && swatchIndex === 0) ? ' is-active' : ''}"
                type="button"
                aria-label="${escapeHtml(swatch.label || 'Color')}"
                title="${escapeHtml(swatch.label || 'Color')}"
                style="--mb-apple-swatch-color: ${escapeHtml(swatch.color || getColorFromName(swatch.label))};"
                data-mb-apple-compare-swatch
                data-column-index="${columnIndex}"
                data-product-id="${escapeHtml(product.id)}"
                data-variant-id="${escapeHtml(swatchVariantId)}"
              ></button>
            `;
          }).join('')}
        </div>

        ${renderProductOptionSelect(product, activeVariant, columnIndex, orientationOptionIndex, 'Select orientation')}

        ${renderProductOptionSelect(product, activeVariant, columnIndex, configurationOptionIndex, 'Select configuration')}

        ${warrantyOptionIndex > -1
          ? renderProductOptionSelect(product, activeVariant, columnIndex, warrantyOptionIndex, 'Select warranty')
          : variants.length > 1 ? `
            <select
              class="mb-apple-compare-product__variant"
              data-mb-apple-compare-variant-select
              data-column-index="${columnIndex}"
              data-product-id="${escapeHtml(product.id)}"
              aria-label="Select variant for ${escapeHtml(product.title || '')}"
            >
              ${variants.map((variant) => `
                <option value="${escapeHtml(variant.id)}"${normalizeId(variant.id) === normalizeId(activeVariant?.id) ? ' selected' : ''}${variant.available === false ? ' disabled' : ''}>
                  ${escapeHtml(variant.title || 'Default')}
                </option>
              `).join('')}
            </select>
          ` : ''
        }

        <p class="mb-apple-compare-product__price">
          <span data-mb-apple-compare-price>${escapeHtml(price)}</span>
          ${comparePrice ? `<span class="mb-apple-compare-product__compare-price">${escapeHtml(comparePrice)}</span>` : ''}
        </p>

        <div class="mb-apple-compare-product__actions">
          <button
            class="mb-apple-compare-product__buy"
            type="button"
            data-mb-apple-compare-add-to-cart
            data-variant-id="${escapeHtml(activeVariant?.id || '')}"
            ${activeVariant?.available === false ? ' disabled' : ''}
          >
            ${activeVariant?.available === false ? 'Sold out' : 'Add to cart'}
          </button>

          <a class="mb-apple-compare-product__learn" href="${escapeHtml(product.url || '#')}">
            Learn more ›
          </a>
        </div>
      </article>
    `;
  }

  function renderSpecifications(limit) {
    if (!dom.specs) return;

    const products = MBAppleCompare.activeColumnIds
      .slice(0, limit)
      .map((id) => MBAppleCompare.productsById.get(normalizeId(id)) || null);

    const orderedSpecs = [];
    const seenSpecs = new Set();

    products.forEach((product) => {
      if (!product) return;

      getProductSpecs(product).forEach((spec) => {
        const key = String(spec.key || spec.label).toLowerCase().trim();
        if (!key) return;

        if (seenSpecs.has(key)) {
          const existingSpec = orderedSpecs.find((orderedSpec) => orderedSpec.key === key);
          if (existingSpec && !Number.isFinite(existingSpec.order) && Number.isFinite(spec.order)) {
            existingSpec.order = spec.order;
          }
          return;
        }

        seenSpecs.add(key);
        orderedSpecs.push({
          discoveryIndex: orderedSpecs.length,
          key,
          label: spec.label,
          order: spec.order,
        });
      });
    });

    orderedSpecs.sort((firstSpec, secondSpec) => {
      const firstHasOrder = Number.isFinite(firstSpec.order);
      const secondHasOrder = Number.isFinite(secondSpec.order);

      if (firstHasOrder && secondHasOrder && firstSpec.order !== secondSpec.order) {
        return firstSpec.order - secondSpec.order;
      }
      if (firstHasOrder !== secondHasOrder) return firstHasOrder ? -1 : 1;
      return firstSpec.discoveryIndex - secondSpec.discoveryIndex;
    });

    if (orderedSpecs.length === 0) {
      dom.specs.innerHTML = '';
      return;
    }

    dom.specs.innerHTML = orderedSpecs.map((specRow) => {
      return `
        <div class="mb-apple-compare-spec-row" style="--mb-apple-compare-columns: ${limit};">
          ${products.map((product) => {
            const productSpecs = product ? getProductSpecs(product) : [];
            const matchedSpec = productSpecs.find((spec) => {
              return String(spec.key || spec.label).toLowerCase().trim() === specRow.key;
            });

            const value = matchedSpec?.value || '—';

            return `
              <div class="mb-apple-compare-spec-cell">
                <div class="mb-apple-compare-spec-cell__value${value === '—' ? ' mb-apple-compare-spec-cell__empty' : ''}">
                  ${escapeHtml(value)}
                </div>
                <div class="mb-apple-compare-spec-cell__label">
                  ${escapeHtml(specRow.label)}
                </div>
              </div>
            `;
          }).join('')}
        </div>
      `;
    }).join('');
  }

  function handleColumnSelect(event) {
    const select = closestFromEvent(event, '[data-mb-apple-compare-column-select]');
    if (!select) return;

    const columnIndex = Number(select.dataset.mbAppleCompareColumnSelect);
    const selectedProductId = normalizeId(select.value);

    MBAppleCompare.activeColumnIds[columnIndex] = selectedProductId;

    syncSelectionFromColumns();
    renderModal();
    syncCheckboxes();
    renderBar();
  }

  function syncSelectionFromColumns() {
    const limit = getCurrentLimit();
    const uniqueIds = [];

    MBAppleCompare.activeColumnIds.slice(0, limit).forEach((id) => {
      const cleanId = normalizeId(id);
      if (cleanId && !uniqueIds.includes(cleanId)) {
        uniqueIds.push(cleanId);
      }
    });

    MBAppleCompare.selectedProductIds = uniqueIds;

    if (uniqueIds.length > 0) {
      const firstProduct = MBAppleCompare.productsById.get(uniqueIds[0]);
      MBAppleCompare.activeCompareGroup = firstProduct ? getProductGroup(firstProduct) : '';
    } else {
      MBAppleCompare.activeCompareGroup = '';
    }

    saveSelection();
  }

  function handleSwatchClick(event) {
    const swatch = closestFromEvent(event, '[data-mb-apple-compare-swatch]');
    if (!swatch) return;

    const product = MBAppleCompare.productsById.get(normalizeId(swatch.dataset.productId));
    if (!product) return;

    const colorOptionIndex = getColorOptionIndex(product);
    const swatchVariant = getVariantById(product, swatch.dataset.variantId);

    if (colorOptionIndex > -1 && swatchVariant) {
      const colorValue = getVariantOptionValue(swatchVariant, colorOptionIndex);
      const matchedVariant = findVariantForOptionChange(product, colorOptionIndex, colorValue);
      setSelectedVariant(product, matchedVariant || swatchVariant);
    } else {
      setSelectedVariant(product, swatchVariant);
    }

    renderModal();
  }

  function handleOptionSelectChange(event) {
    const select = closestFromEvent(event, '[data-mb-apple-compare-option-select]');
    if (!select) return;

    const product = MBAppleCompare.productsById.get(normalizeId(select.dataset.productId));
    if (!product) return;

    const optionIndex = Number(select.dataset.optionIndex);
    const optionValue = select.value;
    const matchedVariant = findVariantForOptionChange(product, optionIndex, optionValue);

    setSelectedVariant(product, matchedVariant || getVariantById(product, product.selectedVariantId));

    renderModal();
  }

  function handleVariantChange(event) {
    const select = closestFromEvent(event, '[data-mb-apple-compare-variant-select]');
    if (!select) return;

    const product = MBAppleCompare.productsById.get(normalizeId(select.dataset.productId));
    if (!product) return;

    const variant = getVariantById(product, select.value);

    setSelectedVariant(product, variant);

    renderModal();
  }

  function getCartSectionIds() {
    const sectionIds = [];

    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (!(component instanceof HTMLElement)) return;

      const sectionId = component.dataset.sectionId;
      if (sectionId && !sectionIds.includes(sectionId)) {
        sectionIds.push(sectionId);
      }
    });

    return sectionIds;
  }

  function getFallbackCartItemCount() {
    const countElement = document.querySelector('.cart-bubble__text-count');
    const currentCount = parseInt(countElement?.textContent || '', 10);

    if (!Number.isNaN(currentCount) && currentCount >= 0) {
      return currentCount + 1;
    }

    return 1;
  }

  function dispatchThemeCartUpdate(cart, variantId, sections) {
    const itemCount = typeof cart?.item_count === 'number' ? cart.item_count : getFallbackCartItemCount();

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart || {},
          sourceId: String(variantId || ''),
          data: {
            source: 'mb-apple-compare',
            variantId: String(variantId || ''),
            itemCount,
            sections: sections || {},
          },
        },
      })
    );
  }

  async function addToCart(variantId, button) {
    if (!variantId) return;

    const originalText = button?.textContent || 'Add to cart';

    try {
      if (button) {
        button.disabled = true;
        button.textContent = 'Adding...';
      }

      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', '1');

      const sectionIds = getCartSectionIds();
      if (sectionIds.length > 0) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const addResponse = await fetch((window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: formData,
      });

      const addData = await addResponse.json();

      if (!addResponse.ok || addData.status) {
        throw new Error(addData.message || 'Unable to add item to cart');
      }

      let cart = null;

      try {
        const cartResponse = await fetch('/cart.js', {
          headers: {
            Accept: 'application/json',
          },
        });

        if (cartResponse.ok) {
          cart = await cartResponse.json();
        }
      } catch (_) {
        cart = null;
      }

      dispatchThemeCartUpdate(cart, variantId, addData.sections);

      if (button) {
        button.textContent = 'Added';
        window.setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 1200);
      }
    } catch (_) {
      if (button) {
        button.textContent = 'Try again';
        window.setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 1400);
      } else {
        alert('Could not add to cart');
      }
    }
  }

  function handleAddToCartClick(event) {
    const button = closestFromEvent(event, '[data-mb-apple-compare-add-to-cart]');
    if (!button) return;

    event.preventDefault();
    addToCart(button.dataset.variantId, button);
  }

  function handleResize() {
    const previousLimit = dom.limit?.textContent;
    const nextLimit = String(getCurrentLimit());

    if (previousLimit !== nextLimit) {
      syncCheckboxes();
      renderBar();

      if (isModalOpen()) {
        openModal();
      }
    }
  }

  function refreshAfterSectionLoad() {
    collectAllProducts();
    loadSelection();
    bindCompareControlGuards();
    syncCheckboxes();
    renderBar();

    if (isModalOpen()) {
      renderModal();
    }
  }

  function scheduleRefresh() {
    window.clearTimeout(MBAppleCompare.refreshTimer);
    MBAppleCompare.refreshTimer = window.setTimeout(refreshAfterSectionLoad, 50);
  }

  function bindCompareControlGuards(root = document) {
    root.querySelectorAll('.mb-cowboy-card-compare').forEach((control) => {
      if (control.dataset.mbAppleCompareGuardBound === 'true') return;

      control.dataset.mbAppleCompareGuardBound = 'true';

      control.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
      });

      control.addEventListener('click', (event) => {
        handleCompareLabelClick(event);
        event.stopPropagation();
      });
    });
  }

  function bindEvents() {
    bindCompareControlGuards();

    document.addEventListener('change', handleCheckboxChange);
    document.addEventListener('click', handleCompareLabelClick);

    if (dom.openButton) {
      dom.openButton.addEventListener('click', openModal);
    }

    if (dom.clearButton) {
      dom.clearButton.addEventListener('click', (event) => {
        event.preventDefault();
        clearSelection();
      });
    }

    dom.closeButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        closeModal();
      });
    });

    document.addEventListener('change', handleColumnSelect);
    document.addEventListener('click', handleSwatchClick);
    document.addEventListener('change', handleOptionSelectChange);
    document.addEventListener('change', handleVariantChange);
    document.addEventListener('click', handleAddToCartClick);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isModalOpen()) {
        closeModal();
      }
    });

    window.addEventListener('resize', () => {
      window.clearTimeout(MBAppleCompare.resizeTimer);
      MBAppleCompare.resizeTimer = window.setTimeout(handleResize, 160);
    });

    document.addEventListener('shopify:section:load', () => {
      scheduleRefresh();
    });

    document.addEventListener('mb:product-cards:updated', (event) => {
      if (event.detail?.immediate === true) {
        window.clearTimeout(MBAppleCompare.refreshTimer);
        refreshAfterSectionLoad();
        return;
      }

      scheduleRefresh();
    });
  }

  function init() {
    cacheDom();

    if (!dom.root) return;

    collectAllProducts();
    loadSelection();
    bindEvents();
    syncCheckboxes();
    renderBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
